const defaultApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return '';
  }
  return 'http://localhost:17110';
};

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || defaultApiBaseUrl();
export const TOKEN_STORAGE_KEY = 'seeme-auth-token';
export const SHARED_AUTH_COOKIE_NAME = 'ximi-auth-token';
const authFailureListeners = new Set();

const sharedCookieDomain = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  const domain = ['365ybgame.com', 'ybinfor.com'].find(name => window.location.hostname.endsWith(`.${name}`));
  return domain ? `; Domain=.${domain}` : '';
};

const writeCookieToken = (token) => {
  if (typeof document === 'undefined') {
    return;
  }
  const domain = sharedCookieDomain();
  if (domain) {
    document.cookie = `${SHARED_AUTH_COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax`;
  }
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  if (!token) {
    document.cookie = `${SHARED_AUTH_COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax${domain}${secure}`;
    return;
  }
  document.cookie = `${SHARED_AUTH_COOKIE_NAME}=${encodeURIComponent(token)}; Max-Age=2592000; Path=/; SameSite=Lax${domain}${secure}`;
};

const readCookieToken = () => {
  if (typeof document === 'undefined') {
    return null;
  }
  const rawToken = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${SHARED_AUTH_COOKIE_NAME}=`))
    ?.split('=')
    .slice(1)
    .join('=');
  return rawToken ? decodeURIComponent(rawToken) : null;
};

export const readAuthToken = () => {
  try {
    const localToken = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (localToken) {
      writeCookieToken(localToken);
      return localToken;
    }
    const cookieToken = readCookieToken();
    if (cookieToken) {
      window.localStorage.setItem(TOKEN_STORAGE_KEY, cookieToken);
      return cookieToken;
    }
    return null;
  } catch (error) {
    return readCookieToken();
  }
};

export const writeAuthToken = (token) => {
  try {
    if (token) {
      window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch (error) {
    // Ignore storage failures; the request layer will still work for the current page.
  }
  writeCookieToken(token);
};

export const subscribeAuthFailure = (listener) => {
  authFailureListeners.add(listener);
  return () => authFailureListeners.delete(listener);
};

const notifyAuthFailure = (error) => {
  writeAuthToken(null);
  authFailureListeners.forEach(listener => listener(error));
};

const resolveWebSocketUrl = () => {
  const configured = process.env.REACT_APP_WS_URL;
  if (configured) {
    if (/^wss?:\/\//.test(configured)) {
      return configured;
    }
    const origin = window.location.origin.replace(/^http/, 'ws');
    return `${origin}${configured.startsWith('/') ? configured : `/${configured}`}`;
  }
  if (/^https?:\/\//.test(API_BASE_URL)) {
    return API_BASE_URL.replace(/^http/, 'ws') + '/api/ws';
  }
  const origin = window.location.origin.replace(/^http/, 'ws');
  return `${origin}${API_BASE_URL}/api/ws`;
};

export const WS_URL = resolveWebSocketUrl();

const unwrapBody = (body, path, response) => {
  if (body && typeof body === 'object') {
    if (Object.prototype.hasOwnProperty.call(body, 'ok')) {
      if (!body.ok) {
        throw new Error(body.message || `API failed: ${path}`);
      }
      return body.data;
    }
    if (Object.prototype.hasOwnProperty.call(body, 'success')) {
      if (!body.success) {
        throw new Error(body.message || `API failed: ${path}`);
      }
      return body.data;
    }
  }
  if (!response.ok) {
    throw new Error(body?.message || body?.detail || `API ${response.status}: ${path}`);
  }
  return body?.data ?? body;
};

async function request(path, options = {}) {
  const headers = new Headers(options.headers || {});
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }
  const token = readAuthToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', token);
  }
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.message || body.detail || `API ${response.status}: ${path}`);
    error.status = response.status;
    error.path = path;
    if (response.status === 401 && token) {
      notifyAuthFailure(error);
    }
    throw error;
  }
  return unwrapBody(body, path, response);
}

async function uploadRequest(path, file) {
  const formData = new FormData();
  formData.append('file', file);
  const headers = new Headers();
  const token = readAuthToken();
  if (token) {
    headers.set('Authorization', token);
  }
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.message || body.detail || `API ${response.status}: ${path}`);
    error.status = response.status;
    error.path = path;
    if (response.status === 401 && token) {
      notifyAuthFailure(error);
    }
    throw error;
  }
  const data = unwrapBody(body, path, response);
  return {
    ...data,
    url: data.url?.startsWith('http') ? data.url : `${API_BASE_URL}${data.url}`,
  };
}

export const api = {
  login: (payload) => request('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  logout: () => request('/api/auth/logout', { method: 'DELETE' }),
  me: () => request('/api/auth/me'),
  listConversations: (status = 'open') => request(`/api/message/conversations?status=${encodeURIComponent(status)}`),
  createConversation: (payload) => request('/api/message/conversations', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  listMessages: (conversationId) => request(`/api/message/conversations/${conversationId}/messages`),
  getWeComConversationState: (conversationId) => request(`/api/message/conversations/${conversationId}/wecom-state`),
  markRead: (conversationId) => request(`/api/message/conversations/${conversationId}/read`, { method: 'POST' }),
  closeConversation: (conversationId) => request(`/api/message/conversations/${conversationId}/close`, { method: 'POST' }),
  reopenConversation: (conversationId) => request(`/api/message/conversations/${conversationId}/reopen`, { method: 'POST' }),
  sendMessage: (conversationId, message) => request(`/api/message/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: JSON.stringify(typeof message === 'string' ? { text: message } : message),
  }),
  sendQuote: (conversationId, quote) => request(`/api/message/conversations/${conversationId}/quote`, {
    method: 'POST',
    body: JSON.stringify(quote),
  }),
  translateMessage: (conversationId, messageId, targetLang = 'zh') => request(`/api/message/conversations/${conversationId}/messages/${messageId}/translate`, {
    method: 'POST',
    body: JSON.stringify({ targetLang }),
  }),
  translate: (text, targetLang = 'en') => request('/api/tools/translate', {
    method: 'POST',
    body: JSON.stringify({ text, targetLang }),
  }),
  uploadFile: (file) => uploadRequest('/api/uploads', file),
  listTasks: () => request('/api/message/tasks'),
  completeTask: (taskId) => request(`/api/message/tasks/${taskId}`, {
    method: 'DELETE',
  }),
  listContacts: () => request('/api/message/contacts'),
  createContact: (contact) => request('/api/message/contacts', {
    method: 'POST',
    body: JSON.stringify(contact),
  }),
  getAnalyticsSummary: () => request('/api/message/analytics/summary'),
  getTelegramStatus: () => request('/api/channel/telegram/status'),
  getTelegramBotSettings: () => request('/api/channel/telegram/bot/settings'),
  saveTelegramBotSettings: (payload) => request('/api/channel/telegram/bot/settings', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  configureTelegramBotWebhook: (webhookUrl) => request('/api/channel/telegram/bot/webhook', {
    method: 'POST',
    body: JSON.stringify({ webhookUrl }),
  }),
  getEmailSettings: () => request('/api/channel/email/settings'),
  saveEmailSettings: (payload) => request('/api/channel/email/settings', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  listEmailTemplates: () => request('/api/channel/email/templates'),
  createEmailTemplate: (payload) => request('/api/channel/email/templates', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  updateEmailTemplate: (templateId, payload) => request(`/api/channel/email/templates/${templateId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  }),
  deleteEmailTemplate: (templateId) => request(`/api/channel/email/templates/${templateId}`, {
    method: 'DELETE',
  }),
  getEmailStatus: () => request('/api/channel/email/status'),
  pollEmailInbox: () => request('/api/channel/email/poll', { method: 'POST' }),
  getWhatsAppSettings: () => request('/api/channel/whatsapp/settings'),
  saveWhatsAppSettings: (payload) => request('/api/channel/whatsapp/settings', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  listWhatsAppPersonalSessions: () => request('/api/channel/whatsapp-personal/client-sessions'),
  createWhatsAppPersonalSession: (label) => request('/api/channel/whatsapp-personal/client-sessions', {
    method: 'POST',
    body: JSON.stringify({ label }),
  }),
  renameWhatsAppPersonalSession: (sessionId, label) => request(`/api/channel/whatsapp-personal/client-sessions/${sessionId}/rename`, {
    method: 'POST',
    body: JSON.stringify({ label }),
  }),
  getWhatsAppPersonalQr: (sessionId) => request(`/api/channel/whatsapp-personal/client-sessions/${sessionId}/qr`),
  closeWhatsAppPersonalSession: (sessionId) => request(`/api/channel/whatsapp-personal/client-sessions/${sessionId}`, {
    method: 'DELETE',
  }),
  deleteWhatsAppPersonalSession: (sessionId) => request(`/api/channel/whatsapp-personal/client-sessions/${sessionId}/record`, {
    method: 'DELETE',
  }),
  getTeamsSettings: () => request('/api/channel/teams/settings'),
  saveTeamsSettings: (payload) => request('/api/channel/teams/settings', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  getTeamsOAuthUrl: () => request('/api/channel/teams/oauth/url'),
  getWeComStatus: () => request('/api/channel/wecom/status'),
  getWeComMetrics: () => request('/api/channel/wecom/metrics'),
  getWeComSettings: () => request('/api/channel/wecom/settings'),
  saveWeComSettings: (payload) => request('/api/channel/wecom/settings', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  listWeComAccounts: () => request('/api/channel/wecom/kf/accounts'),
  syncWeComAccounts: () => request('/api/channel/wecom/kf/accounts/sync', { method: 'POST' }),
  saveWeComAccountAssignee: (openKfid, assigneeId) => request(`/api/channel/wecom/kf/accounts/${encodeURIComponent(openKfid)}/assignee`, {
    method: 'POST',
    body: JSON.stringify({ assigneeId }),
  }),
  listWeComMembers: () => request('/api/channel/wecom/members'),
  syncWeComMembers: () => request('/api/channel/wecom/members/sync', { method: 'POST' }),
  bindWeComMember: (userid, erpUserId) => request(`/api/channel/wecom/members/${encodeURIComponent(userid)}/binding`, {
    method: 'POST',
    body: JSON.stringify({ erpUserId: erpUserId || null }),
  }),
  listWeComExternalContactWays: () => request('/api/channel/wecom/external-contact/contact-ways'),
  createWeComExternalContactWay: (payload) => request('/api/channel/wecom/external-contact/contact-ways', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  createWeComContactWay: (payload) => request('/api/channel/wecom/kf/contact-way', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  triggerWeComSync: (payload) => request('/api/channel/wecom/kf/sync', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  listTelegramClientSessions: () => request('/api/channel/telegram/client-sessions'),
  createTelegramClientSession: (label) => request('/api/channel/telegram/client-sessions', {
    method: 'POST',
    body: JSON.stringify({ label }),
  }),
  renameTelegramClientSession: (sessionId, label) => request(`/api/channel/telegram/client-sessions/${sessionId}/rename`, {
    method: 'POST',
    body: JSON.stringify({ label }),
  }),
  submitTelegramClientPhone: (sessionId, phoneNumber) => request(`/api/channel/telegram/client-sessions/${sessionId}/phone`, {
    method: 'POST',
    body: JSON.stringify({ phoneNumber }),
  }),
  submitTelegramClientCode: (sessionId, code) => request(`/api/channel/telegram/client-sessions/${sessionId}/code`, {
    method: 'POST',
    body: JSON.stringify({ code }),
  }),
  submitTelegramClientPassword: (sessionId, password) => request(`/api/channel/telegram/client-sessions/${sessionId}/password`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  }),
  closeTelegramClientSession: (sessionId) => request(`/api/channel/telegram/client-sessions/${sessionId}`, {
    method: 'DELETE',
  }),
  deleteTelegramClientSession: (sessionId) => request(`/api/channel/telegram/client-sessions/${sessionId}/record`, {
    method: 'DELETE',
  }),
  searchTelegramContacts: (sessionId, query, limit = 10) => request(`/api/channel/telegram/client-sessions/${sessionId}/contacts/search`, {
    method: 'POST',
    body: JSON.stringify({ query, limit }),
  }),
  addTelegramContact: (sessionId, payload) => request(`/api/channel/telegram/client-sessions/${sessionId}/contacts`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  listPermissionUsers: () => request('/api/modules/permissions/users'),
  updateContact: (contactId, updates) => request(`/api/message/contacts/${contactId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  }),
  deleteContact: (contactId) => request(`/api/message/contacts/${contactId}`, {
    method: 'DELETE',
  }),
  addReminder: (contactId, reminder) => request(`/api/message/contacts/${contactId}/reminders`, {
    method: 'POST',
    body: JSON.stringify(reminder),
  }),
  removeReminder: (contactId, reminderId) => request(`/api/message/contacts/${contactId}/reminders/${reminderId}`, {
    method: 'DELETE',
  }),
  addOrder: (contactId, order) => request(`/api/message/contacts/${contactId}/orders`, {
    method: 'POST',
    body: JSON.stringify(order),
  }),
  removeOrder: (contactId, orderId) => request(`/api/message/contacts/${contactId}/orders/${orderId}`, {
    method: 'DELETE',
  }),
};
